import { Injectable } from '@angular/core';
import { filterNilValue, Store, StoreConfig } from '@datorama/akita';
import { LocationStrategy } from '@angular/common';
import { SessionQuery } from '../../../core/state/session/session.query';
import { tap } from 'rxjs/operators';
import { AppConfigQuery } from '../../../core/state/app-config/app-config.query';
import { SbEventBusService, SbEventTopic } from '@services';
import { Currency } from '../../../shared/components/currency-selector/currency-country-codes';

export type CurrenciesAndRates = { [currency in Currency]: number };
export interface SettingsState {
    baseCurrency: string;
    currenciesAndRates?: CurrenciesAndRates;
    theme: 'light' | 'dark' | 'auto' | string;
    digitsPercent: [number, number];
    digitsSmall: [number, number];
    digitsLarge: [number, number];
    currencySymbol: 'code' | 'symbol' | 'symbol-narrow';
    locale: string;
}

export const supportedLocales = ['no', 'nb', 'sv', 'en-GB', 'en', 'fi', 'da'];
export const defaultLocale = 'sv';

const cyrb53 = (str, seed = 0) => {
    let h1 = 0xdeadbeef ^ seed,
        h2 = 0x41c6ce57 ^ seed;
    for (let i = 0, ch; i < str.length; i++) {
        ch = str.charCodeAt(i);
        h1 = Math.imul(h1 ^ ch, 2654435761);
        h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 =
        Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 =
        Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    return 4294967296 * (2097151 & h2) + (h1 >>> 0) + '';
};

export const generateStorageKey = (basePath, userId) => cyrb53(`${basePath}.${userId}`);

export const getBrowserLocale = () => {
    let langArray = navigator.language.split('-');

    // support for en-US and en-GB variants - if any other en-XX fallback to en-GB
    if (langArray?.length === 2 && langArray[0] === 'en') {
        switch (langArray[1]) {
            case 'US':
                return navigator.language;
            default:
                return 'en-GB';
        }
    } else
        return (
            navigator.language.substring(0, navigator.language.indexOf('-')) ||
            navigator.language
        );
};

export const DefaultSettings: SettingsState = {
    baseCurrency: 'SEK',
    theme: 'light', // 'auto',
    digitsPercent: [0, 2],
    digitsSmall: [0, 4],
    digitsLarge: [0, 2],
    currencySymbol: 'code',
    locale:
        supportedLocales.indexOf(getBrowserLocale()) > -1
            ? getBrowserLocale()
            : defaultLocale
};

export function createLoggedInState(basePath: string, userId: string): SettingsState {
    const savedState = JSON.parse(
        localStorage.getItem(generateStorageKey(basePath, userId)) || '{}'
    );
    return {
        ...DefaultSettings,
        ...savedState
    };
}

export const InitialSettings: SettingsState = {
    baseCurrency: null,
    theme: 'light', // 'auto',
    digitsPercent: null,
    digitsSmall: null,
    digitsLarge: null,
    currencySymbol: null,
    locale:
        supportedLocales.indexOf(getBrowserLocale()) > -1
            ? getBrowserLocale()
            : defaultLocale
};

export function createInitialState(settings?: Partial<SettingsState>): SettingsState {
    return {
        ...InitialSettings,
        ...settings
    };
}

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'settings' })
export class SettingsStore extends Store<SettingsState> {
    constructor(
        private locationStrategy: LocationStrategy,
        private sessionQuery: SessionQuery,
        private appConfig: AppConfigQuery,
        private eventBus: SbEventBusService
    ) {
        super(
            createInitialState({
                theme: appConfig.get()?.cssTheme?.replace(/-theme/, '') || 'light'
            })
        );
        this.sessionQuery.userName$
            .pipe(
                filterNilValue(),
                tap((userId) =>
                    this.update(
                        createLoggedInState(this.locationStrategy.getBaseHref(), userId)
                    )
                )
            )
            .subscribe();
    }
}
